/**
 * Collect unique exchange targets from multi-selected canvas nodes for
 * batch subpoena generation (one notice per exchange).
 */
import { safeStr } from './safeStr';

export interface BatchNoticeTarget {
    exchange_name: string;
    address: string;
    chain: string;
    wallet_role?: string | null;
    attribution_method?: string | null;
    attribution_tier?: string | null;
    is_offramp_point?: boolean;
    source: 'entity' | 'offramp' | 'label';
}

function isExchangeLike(node: any): boolean {
    const t = String(node?.entity_type || node?.entity_category || '').toLowerCase();
    return t === 'exchange' || t === 'vasp' || t === 'high risk exchange' || t === 'high_risk_exchange';
}

/** Deduplicate by normalized exchange name; first address wins, extras listed. */
export function collectBatchNoticeTargets(nodes: any[]): {
    targets: BatchNoticeTarget[];
    duplicatesCollapsed: number;
} {
    const byExchange = new Map<string, BatchNoticeTarget & { related: string[] }>();
    let duplicatesCollapsed = 0;

    for (const node of nodes || []) {
        if (!node?.id) continue;
        const chain = safeStr(node.chain) || 'ETH';
        const candidates: Array<{ name: string; source: BatchNoticeTarget['source']; offramp?: boolean }> = [];

        if (isExchangeLike(node) && node.entity_name) {
            candidates.push({ name: safeStr(node.entity_name), source: 'entity' });
        }

        const summary = node.triage_summary || node.summary || null;
        const offramps: string[] = Array.isArray(summary?.offramp_exchanges)
            ? summary.offramp_exchanges
            : Array.isArray(node.offramp_exchanges)
                ? node.offramp_exchanges
                : [];
        for (const ex of offramps) {
            const name = safeStr(ex);
            if (name) candidates.push({ name, source: 'offramp', offramp: true });
        }

        // Label fallback: entity_name when category hints CEX
        if (!candidates.length && node.entity_name && /exchange|binance|wazirx|okx|bybit|htx|coindcx|zebpay/i.test(String(node.entity_name))) {
            candidates.push({ name: safeStr(node.entity_name), source: 'label' });
        }

        for (const c of candidates) {
            if (!c.name) continue;
            const key = c.name.toLowerCase();
            const existing = byExchange.get(key);
            if (existing) {
                duplicatesCollapsed += 1;
                if (!existing.related.includes(node.id)) existing.related.push(node.id);
                continue;
            }
            byExchange.set(key, {
                exchange_name: c.name,
                address: node.id,
                chain,
                wallet_role: summary?.offramp_wallet_role ?? summary?.wallet_role ?? null,
                attribution_method: summary?.attribution_method ?? null,
                attribution_tier: summary?.attribution_tier ?? null,
                is_offramp_point: Boolean(c.offramp || summary?.is_offramp_point),
                source: c.source,
                related: [node.id],
            });
        }
    }

    const targets: BatchNoticeTarget[] = Array.from(byExchange.values()).map(({ related, ...rest }) => ({
        ...rest,
        // related addresses beyond the primary are carried for the API
        ...(related.length > 1 ? { related_addresses: related } as any : {}),
    }));

    return { targets, duplicatesCollapsed };
}
